
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import Index from '@/pages/Index';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Profile from '@/pages/Profile';
import ProfileEdit from '@/pages/ProfileEdit';
import ProfileCompletionWizard from '@/pages/ProfileCompletionWizard';
import UserProfile from '@/pages/UserProfile';
import InternalProfile from '@/pages/InternalProfile';
import Dashboard from '@/pages/Dashboard';
import Teams from '@/pages/Teams';
import MyTeams from '@/pages/MyTeams';
import TeamManagement from '@/pages/TeamManagement';
import Chat from '@/pages/Chat';
import Mentions from '@/pages/Mentions';
import Points from '@/pages/Points';
import BuyPoints from '@/pages/BuyPoints';
import PointsPurchaseSuccess from '@/pages/PointsPurchaseSuccess';
import Deals from '@/pages/Deals';
import FAQ from '@/pages/FAQ';
import PrivacySettings from '@/pages/PrivacySettings';
import PolicyCenter from '@/pages/PolicyCenter';
import AccountSettings from '@/pages/AccountSettings';
import ParentalSettings from '@/pages/ParentalSettings';
import InternalDashboard from '@/pages/InternalDashboard';
import AdminTestDashboard from '@/pages/AdminTestDashboard';
import UserDiscovery from '@/pages/UserDiscovery';
import CoachSchedule from '@/pages/CoachSchedule';
import CoachNetwork from '@/pages/CoachNetwork';
import TeamProfile from '@/pages/TeamProfile';
import CoachDeals from '@/pages/CoachDeals';
import CoachAnalytics from '@/pages/CoachAnalytics';
import TeamsSearch from '@/pages/TeamsSearch';
import AdminUserManagement from '@/pages/admin/AdminUserManagement';
import AdminSystemAnalytics from '@/pages/admin/AdminSystemAnalytics';
import AdminDatabaseTools from '@/pages/admin/AdminDatabaseTools';
import AdminSystemHealth from '@/pages/admin/AdminSystemHealth';
import AdminContentModeration from '@/pages/admin/AdminContentModeration';
import AdminPlatformSettings from '@/pages/admin/AdminPlatformSettings';
import AdminMediaOutlets from '@/pages/admin/AdminMediaOutlets';
import ReengagementCampaigns from '@/pages/admin/ReengagementCampaigns';
import AdminNominations from '@/pages/admin/AdminNominations';
import AdminTransactionManagement from '@/pages/AdminTransactionManagement';
import AudienceMode from '@/pages/AudienceMode';
import AudienceModeTest from '@/pages/AudienceModeTest';
import Community from '@/pages/Community';
import Events from '@/pages/Events';
import Tournaments from '@/pages/Tournaments';
import Support from '@/pages/Support';
import MyTickets from '@/pages/MyTickets';
import CoachDashboard from '@/pages/CoachDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import UserManagement from '@/pages/UserManagement';
import AuthGuard from '@/components/auth/AuthGuard';

import Shop from '@/pages/Shop';
import Connections from '@/pages/Connections';
import VenueTools from '@/pages/VenueTools';
import VenueManagement from '@/pages/VenueManagement';
import VenueClaim from '@/pages/VenueClaim';
import ChildTeams from '@/pages/ChildTeams';
import GameDiscovery from '@/pages/GameDiscovery';
import AdminClaimManagement from '@/pages/AdminClaimManagement';
import GameManagement from '@/pages/GameManagement';
import { GameActionHandler } from '@/pages/GameActionHandler';
import GameDetail from '@/pages/GameDetail';
import GameDetails from '@/pages/GameDetails';
import { UnifiedGameFormPage } from '@/pages/UnifiedGameFormPage';
import { MyGameFormsPage } from '@/pages/MyGameFormsPage';
import Games from '@/pages/Games';
import GameStats from '@/pages/GameStats';
import CreateGame from '@/pages/CreateGame';
import VenueDiscovery from '@/pages/VenueDiscovery';
import AccountUpgrade from '@/pages/AccountUpgrade';
import ScorekeeperDashboard from '@/pages/ScorekeeperDashboard';
import ScorekeeperApplications from '@/pages/ScorekeeperApplications';
import ScorekeeperAssignments from '@/pages/ScorekeeperAssignments';
import ScorekeeperSupportTickets from '@/pages/ScorekeeperSupportTickets';
import ScoreApprovals from '@/pages/ScoreApprovals';
import LiveScoring from '@/pages/LiveScoring';
import { VenuePage } from '@/pages/VenuePage';
import VenueProfile from '@/pages/VenueProfile';
import TeamPlayers from '@/pages/TeamPlayers';
import TeamCoaches from '@/pages/TeamCoaches';
import PlayerStats from '@/pages/PlayerStats';
import AnalyticsDashboard from '@/pages/AnalyticsDashboard';
import Feed from '@/pages/Feed';
import FindPeople from '@/pages/FindPeople';
import ManageFollowing from '@/pages/ManageFollowing';
import ChildManagement from '@/pages/ChildManagement';
import Highlights from '@/pages/Highlights';
import InternalRegistration from '@/pages/InternalRegistration';
import InternalRegistrationSuccess from '@/pages/InternalRegistrationSuccess';
import Featured from '@/pages/Featured';
import TrainingSessions from '@/pages/TrainingSessions';
import TrainingSessionDetail from '@/pages/TrainingSessionDetail';
import CreateLesson from '@/pages/CreateLesson';
import AgentBetaLanding from '@/pages/AgentBetaLanding';
import ManagePlayers from '@/pages/ManagePlayers';
import Home from '@/pages/Home';
import CountryNews from '@/pages/CountryNews';
import Nominate from '@/pages/Nominate';
import MyNominations from '@/pages/MyNominations';
import AgentPricing from '@/pages/AgentPricing';
import AgentDashboard from '@/pages/AgentDashboard';
import AgentTerms from '@/pages/AgentTerms';
import AgentAthleteDiscovery from '@/pages/AgentAthleteDiscovery';
import AgentMessaging from '@/pages/AgentMessaging';
import AgentCalendar from '@/pages/AgentCalendar';
import AgentDeals from '@/pages/AgentDeals';
import AgentApplicationStatus from '@/pages/AgentApplicationStatus';
import AdminFoundingAgentApplications from '@/pages/admin/AdminFoundingAgentApplications';
import VerifyParentalConsent from '@/pages/VerifyParentalConsent';
import ParentConsent from '@/pages/ParentConsent';
import { AuthProvider } from '@/hooks/useAuth';
import { InternalAuthProvider } from '@/hooks/useInternalAuth';
import { ThemeProvider } from '@/components/ThemeProvider';
import { PolicyAcknowledgmentGuard } from '@/components/legal/PolicyAcknowledgmentGuard';
import { HelmetProvider } from 'react-helmet-async';

import './App.css';

const queryClient = new QueryClient();

function App() {
  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <InternalAuthProvider>
            <PolicyAcknowledgmentGuard>
              <Router>
                <div className="App">
                <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/home" element={<Home />} />
                <Route path="/nominate" element={<Nominate />} />
                <Route path="/my-nominations" element={<MyNominations />} />
                <Route path="/country/:countrySlug" element={<CountryNews />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/internal-registration" element={<InternalRegistration />} />
                <Route path="/internal-registration-success" element={<InternalRegistrationSuccess />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile-edit" element={<ProfileEdit />} />
                <Route path="/profile/complete" element={<ProfileCompletionWizard />} />
                <Route path="/internal-profile" element={<InternalProfile />} />
                <Route path="/internal-dashboard" element={<InternalDashboard />} />
                <Route path="/admin-test-dashboard" element={<AdminTestDashboard />} />
                
                {/* Admin Routes */}
                <Route path="/admin/users" element={<AdminUserManagement />} />
                <Route path="/admin/analytics" element={<AdminSystemAnalytics />} />
                <Route path="/admin/database" element={<AdminDatabaseTools />} />
                <Route path="/admin/system-health" element={<AdminSystemHealth />} />
                <Route path="/admin/content-moderation" element={<AdminContentModeration />} />
                <Route path="/admin/test-dashboard" element={<AdminTestDashboard />} />
                <Route path="/admin/platform-settings" element={<AdminPlatformSettings />} />
                <Route path="/admin/venue-claims" element={<AdminClaimManagement />} />
                <Route path="/admin/media-outlets" element={<AdminMediaOutlets />} />
                <Route path="/admin/reengagement" element={<ReengagementCampaigns />} />
                <Route path="/admin/transactions" element={<AdminTransactionManagement />} />
                <Route path="/admin/nominations" element={<AdminNominations />} />
                
                {/* Audience Mode */}
                <Route path="/audience-mode" element={<AudienceMode />} />
                <Route path="/audience-mode/:gameId" element={<AudienceMode />} />
                <Route path="/audience-mode-test" element={<AudienceModeTest />} />
                
                 <Route path="/teams" element={<Teams />} />
                 <Route path="/my-teams" element={<MyTeams />} />
                 <Route path="/team-management" element={<TeamManagement />} />
                 <Route path="/child-teams" element={<ChildTeams />} />
                 <Route path="/teams-search" element={<TeamsSearch />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/chat/:groupId" element={<Chat />} />
                <Route path="/mentions" element={<Mentions />} />
                <Route path="/points" element={<Points />} />
                <Route path="/points/buy" element={<BuyPoints />} />
                <Route path="/points/purchase-success" element={<PointsPurchaseSuccess />} />
                <Route path="/deals" element={<Deals />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/privacy" element={<PrivacySettings />} />
                <Route path="/privacy-settings" element={<PrivacySettings />} />
                <Route path="/policy-center" element={<PolicyCenter />} />
                <Route path="/account-settings" element={<AccountSettings />} />
                <Route path="/parental-settings" element={<ParentalSettings />} />
                <Route path="/user-discovery" element={<UserDiscovery />} />
                <Route path="/connections" element={<Connections />} />
                <Route path="/coach-schedule" element={<CoachSchedule />} />
                <Route path="/coach-network" element={<CoachNetwork />} />
                <Route path="/coach-analytics" element={<CoachAnalytics />} />
                <Route path="/training-sessions/create" element={<CreateLesson />} />
                <Route path="/coach-deals" element={<CoachDeals />} />
                <Route path="/manage-players" element={<ManagePlayers />} />
                <Route path="/community" element={<Community />} />
                <Route path="/events" element={<Events />} />
                <Route path="/tournaments" element={<Tournaments />} />
                <Route path="/support" element={<Support />} />
                <Route path="/my-tickets" element={<MyTickets />} />
                
                <Route path="/shop" element={<Shop />} />
                <Route path="/venue-tools" element={<VenueTools />} />
                <Route path="/venue-management" element={<VenueManagement />} />
                <Route path="/venue-claim/:venueId" element={<VenueClaim />} />
                <Route path="/games/discover" element={<GameDiscovery />} />
                <Route path="/venues/discover" element={<VenueDiscovery />} />
                <Route path="/create-game" element={<CreateGame />} />
                <Route path="/game-management" element={<AuthGuard requireAuth><GameManagement /></AuthGuard>} />
                <Route path="/games/:gameId/details" element={<GameDetails />} />
                <Route path="/games/:gameId/unified-form" element={<UnifiedGameFormPage />} />
                <Route path="/my-game-forms" element={<MyGameFormsPage />} />
                <Route path="/games/:gameId/approval" element={<GameActionHandler />} />
                <Route path="/games/:gameSlug/:actionType" element={<GameActionHandler />} />
                <Route path="/games/:gameSlug" element={<GameDetail />} />
                <Route path="/games" element={<Games />} />
                <Route path="/game-stats" element={<GameStats />} />
                <Route path="/account-upgrade" element={<AccountUpgrade />} />
                
                {/* Scorekeeper Routes */}
                <Route path="/scorekeeper/dashboard" element={<ScorekeeperDashboard />} />
                <Route path="/scorekeeper/applications" element={<ScorekeeperApplications />} />
                <Route path="/scorekeeper/assignments" element={<ScorekeeperAssignments />} />
                <Route path="/scorekeeper/support-tickets" element={<ScorekeeperSupportTickets />} />
                <Route path="/score-approvals" element={<ScoreApprovals />} />
                <Route path="/live-scoring/:gameId" element={<LiveScoring />} />
                
                {/* Admin Routes */}
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/user-management" element={<UserManagement />} />
                <Route path="/admin/venue-management" element={<VenueManagement />} />
                
                {/* Coach Tools Routes */}
                <Route path="/team-players" element={<TeamPlayers />} />
                <Route path="/team-coaches" element={<TeamCoaches />} />
                <Route path="/player-stats" element={<PlayerStats />} />
                <Route path="/analytics-dashboard" element={<AnalyticsDashboard />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/find-people" element={<FindPeople />} />
                <Route path="/manage-following" element={<ManageFollowing />} />
                <Route path="/child-management" element={<ChildManagement />} />
                <Route path="/highlights" element={<Highlights />} />
                <Route path="/featured" element={<Featured />} />
                <Route path="/training-sessions" element={<TrainingSessions />} />
                <Route path="/training-sessions/:sessionId" element={<TrainingSessionDetail />} />
                <Route path="/:username/training-sessions/:slug" element={<TrainingSessionDetail />} />

                {/* Agent Routes */}
                <Route path="/agent-beta" element={<AgentBetaLanding />} />
                <Route path="/agent/pricing" element={<AgentPricing />} />
                <Route path="/agent/dashboard" element={<AgentDashboard />} />
                <Route path="/agent/terms" element={<AgentTerms />} />
                <Route path="/agent/athletes" element={<AgentAthleteDiscovery />} />
                <Route path="/agent/messaging" element={<AgentMessaging />} />
                <Route path="/agent/calendar" element={<AgentCalendar />} />
                <Route path="/agent/deals" element={<AgentDeals />} />
                <Route path="/agent/application-status" element={<AgentApplicationStatus />} />
                
                {/* Admin Agent Management */}
                <Route path="/admin/founding-agent-applications" element={<AdminFoundingAgentApplications />} />
                
                {/* Parental Consent Verification */}
                <Route path="/verify-parental-consent" element={<VerifyParentalConsent />} />
                <Route path="/parent-consent" element={<ParentConsent />} />

                <Route path="/venue/:venueSlug" element={<VenueProfile />} />
                <Route path="/team/:slug" element={<TeamProfile />} />
                <Route path="/:userSlug" element={<UserProfile />} />
              </Routes>
                <Toaster />
              </div>
            </Router>
            </PolicyAcknowledgmentGuard>
          </InternalAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
